/**
 * Applies an in-flight change to an issue's sub-issues to the card's sub-issue progress. The
 * server confirms each with the parent's counts, as a `setFields` in `fields.ts`.
 *
 * - `updateEntityFieldItems` on `subIssues`: the issue viewer publishes one for the old and new
 *   parents when a sub-issue is reparented. It holds the sub-issues added and the IDs of those
 *   removed, and the card holds only counts, so this adjusts them as the issue viewer's
 *   `updateParentSubIssues` does. A removed sub-issue's state isn't in the edit, so the old
 *   parent's completed count can stay one high until the next board read.
 * - `deltaEntityFieldValues` on `subIssuesSummary`: closing, reopening or creating a sub-issue
 *   changes the parent's counts directly.
 *
 * Returns the same card when nothing applies, so a card the edit doesn't change keeps its identity.
 */

import type {DeltaEntityFieldValuesOperation, UpdateEntityFieldItemsOperation} from '@github-ui/optimistic-connection'

import type {BoardPayloadItem} from '../board-payload'

export function applySubIssuesEditToCard(
  item: BoardPayloadItem,
  operation: UpdateEntityFieldItemsOperation | DeltaEntityFieldValuesOperation,
): BoardPayloadItem {
  if (operation.type === 'updateEntityFieldItems') {
    if (operation.field !== 'subIssues') return item
    const addedCompleted = operation.upserts.filter(({item: subIssue}) => subIssue.closed === true).length
    return changeProgress(item, operation.countDelta, addedCompleted)
  }
  if (operation.field !== 'subIssuesSummary') return item
  const total = operation.deltas.total ?? 0
  const completed = operation.deltas.completed ?? 0
  if (!Number.isFinite(total) || !Number.isFinite(completed)) return item
  return changeProgress(item, total, completed)
}

function changeProgress(item: BoardPayloadItem, totalDelta: number, completedDelta: number): BoardPayloadItem {
  if (item.type !== 'issue' || (totalDelta === 0 && completedDelta === 0)) return item
  const slot = item.fields['sub-issues-progress']
  if (slot === undefined) return item

  const total = Math.max(0, (slot.value?.total ?? 0) + totalDelta)
  const completed = Math.min(total, Math.max(0, (slot.value?.completed ?? 0) + completedDelta))
  const value = total === 0 ? null : {total, completed, percentCompleted: Math.floor((completed / total) * 100)}

  return {...item, fields: {...item.fields, 'sub-issues-progress': {value}}}
}
